import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TableContainer,
  TextField,
  Typography
} from "@mui/material";
import { ReactComponent as ArrowDown } from "assets/images/ArrowDown.svg";
import { ReactComponent as CloseIcon } from "assets/images/CloseIcon.svg";
import ErrorIcon from "assets/images/warning-2.svg";
import { FullCircularLoader } from "components/core";
import { t } from "i18next";
import MUIDataTable from "mui-datatables";
import { useCallback, useEffect, useState } from "react";
import { CF_URLS } from "services/api/endpoints";
import spacing from "styles/spacing";

import CommonModal from "components/shared/common-modal/CommonModal";
import ConfirmPopup from "components/shared/confirm-popup/ConfirmPopup";
import {
  DEFAULT_DATA_TABLE_OPTIONS,
  errorMsgs,
  type,
  unauthorized,
  VALUE_EMPTY_STRING,
  VALUE_N
} from "utils/constants";
import { buildType } from "utils/constants/disaster-module/EventSearchConstants";
import {
  attributeTypeId,
  containerColumnsList,
  containerNumber,
  openMediaColumnsList,
  programTypes,
  severity,
  systemId,
  volser,
  zero
} from "utils/constants/disaster-module/logicalVaultRequest";
import {
  dataDogErrorLog,
  getAuthenticatedUserBranch,
  getGlobalAttributeValue,
  getResponseData,
  getTheFirestoreErrorMessage,
  handleRefId
} from "utils/helpers";
import { disasterRecoveryStyles } from "./DisasterRecoveryStyles";

let defaultOption = {
  label: t("common.all"),
  value: VALUE_EMPTY_STRING
};

const LogicalVaultRequest = ({
  customerSelected,
  setAddOpenMedia,
  setAddContainer,
  RequestForm
}) => {
  const [logicalVaults, setLogicalVaults] = useState([]);
  const [medisTypes, setMedisTypes] = useState([]);
  const [selectedLogicalVault, setSelectedLogicalVault] = useState(null);
  const [selectedMediaType, setSelectedMediaType] = useState({
    ...defaultOption
  });
  const [programType, setProgramType] = useState(programTypes.openMedia);
  const districtId = getAuthenticatedUserBranch();
  const [errorMessage, setErrorMessage] = useState(VALUE_EMPTY_STRING);
  const [isLoading, setIsLoading] = useState(false);
  const [helperText, setHelperText] = useState("");
  const [containers, setContainers] = useState([]);
  const [openMediaList, setOpenMediaList] = useState([]);
  const [maxDataCount, setMaxDataCount] = useState(0);
  const [errorType, setErrorType] = useState(severity.error);
  const [showGrid, setShowGrid] = useState(false);
  const [isFindDisabled, setIsFindDisabled] = useState(false);
  const [foundFieldsState, setFoundFieldsState] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState(VALUE_EMPTY_STRING);
  const [resData, setResData] = useState(null);
  const [confirmPopupMessage, setConfirmPopupMessage] = useState(null);
  const [confirmPopup, setConfirmPopup] = useState(false);
  const [awaitEvent, setAwaitEvent] = useState(null);
  // handle session timeout and uncought error
  const handleCatchError = (error) => {
    let errorMessg = "";
    if (error?.response?.data === unauthorized) {
      errorMessg = t("printTapeLabel.sessionLoggedOutMessage");
    } else {
      errorMessg = t("printTapeLabel.systemError");
    }
    const { refId, message } = handleRefId(errorMessg);
    dataDogErrorLog(message, "", { error }, refId);
    setErrorMessage(message);
  };

  const handleError = async (errorCode, replaceText) => {
    if (!errorCode) return;
    if (errorCode === errorMsgs.errorCode26095) {
      setErrorType(severity.warning);
    } else {
      setErrorType(severity.error);
    }
    let { errorMsg } = await getTheFirestoreErrorMessage(errorCode);
    if (errorMsg) {
      if (replaceText) {
        if (typeof replaceText === type.string) {
          errorMsg = errorMsg.replaceAll("|", replaceText);
        } else if (Array.isArray(replaceText)) {
          replaceText.forEach((text) => {
            errorMsg = errorMsg.replace("|", text);
          });
        }
      }
      setErrorMessage(errorMsg);
    }
    setIsLoading(false);
  };

  const getSelectedOpenMediaCount = useCallback(() => {
    return openMediaList.filter((om) => !!om.selected)?.length || zero;
  }, [openMediaList]);

  const getSelectedContainersCount = useCallback(() => {
    return containers.filter((c) => !!c.selected)?.length || zero;
  }, [containers]);

  const hasSelected =
    getSelectedOpenMediaCount() !== zero ||
    getSelectedContainersCount() !== zero;

  useEffect(() => {
    let { openMedia, o, c } = programTypes;
    let foundFieldsStateString = JSON.stringify(foundFieldsState);
    let presetFieldsStateString = JSON.stringify({
      lv: selectedLogicalVault?.value || VALUE_EMPTY_STRING,
      mt: selectedMediaType?.value || VALUE_EMPTY_STRING,
      pt: programType === openMedia ? o : c
    });
    if (foundFieldsStateString !== presetFieldsStateString) {
      setIsFindDisabled(false);
    } else {
      setIsFindDisabled(true);
    }
    // eslint-disable-next-line
  }, [selectedLogicalVault, selectedMediaType, programType]);

  useEffect(() => {
    if (districtId) {
      getLabel();
      getLogicalVaults();
      getMaxCountForGrid();
    }
    // eslint-disable-next-line
  }, []);

  const getLabel = async () => {
    setIsLoading(true);
    let { errorMsg: helperText } = await getTheFirestoreErrorMessage(
      errorMsgs.errorCode26101
    );
    setHelperText(helperText);
  };

  const onClickClear = () => {
    setProgramType(programTypes.openMedia);
    setSelectedLogicalVault(null);
    setSelectedMediaType({ ...defaultOption });
    setIsFindDisabled(false);
    setOpenMediaList([]);
    setContainers([]);
    setShowGrid(false);
    setFoundFieldsState({});
    RequestForm.setFieldValue("openMedia", []);
    RequestForm.setFieldValue("container", []);
    RequestForm.setFieldValue("hasLogicalVaultDataToAdd", false);
  };

  const generateForLogicalVault = async () => {
    let { openMedia, o, c } = programTypes;
    setOpenMediaList([]);
    setContainers([]);
    try {
      setIsLoading(true);
      const reqBody = {
        main_district_id: districtId,
        cont_or_om_flag: programType === openMedia ? o : c,
        customer_id: customerSelected,
        logical_vault_id: selectedLogicalVault?.value || VALUE_EMPTY_STRING,
        media_type_id: selectedMediaType?.value || VALUE_EMPTY_STRING,
        show_om_count_only_flag: VALUE_N
      };
      let URL = CF_URLS.disasterRecovery.getListcontOmbyVault;
      const res = await getResponseData(reqBody, URL, 4);
      let data = res.data;
      if (data[0] && data[0][0]?.error && data[0][0]?.error !== zero) {
        handleError(data[0][0]?.error);
        setIsLoading(false);
        return;
      }
      let resultCount = data[0].length;
      if (!resultCount) {
        setShowGrid(false);
        setIsFindDisabled(true);
        await handleError(errorMsgs.errorCode26095);
      }
      if (resultCount > maxDataCount) {
        let { errorMsg } = await getTheFirestoreErrorMessage(
          errorMsgs.errorCode26114
        );
        errorMsg = errorMsg.replace("|", resultCount.toString());
        setConfirmMessage(errorMsg);
        setShowConfirmModal(true);
        setResData(data[0]);
        return;
      }
      mapDataFromRes(data[0]);
    } catch (error) {
      handleCatchError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const mapDataFromRes = (data) => {
    let { openMedia, o, c } = programTypes;

    if (data.length) {
      setFoundFieldsState({
        lv: selectedLogicalVault?.value || VALUE_EMPTY_STRING,
        mt: selectedMediaType?.value || VALUE_EMPTY_STRING,
        pt: programType === openMedia ? o : c
      });
      setShowGrid(true);
      setIsFindDisabled(true);
    }
    if (programType === openMedia) {
      let list = data
        .map((item) => ({
          selected: true,
          mediaType: item.short_descr,
          description: item.om_descr,
          drpCode: item.drp_code,
          openMedia: item.volser?.trim(),
          volser: item.volser?.trim(),
          status: item.om_status,
          ...item
        }))
        .sort((a, b) => a.openMedia.localeCompare(b.openMedia));
      setOpenMediaList(list);
      RequestForm.setFieldValue("openMedia", list);
    } else {
      let list = data
        .map((item) => {
          let number = item.container_number?.trim();
          return {
            selected: true,
            containerNumber: number,
            status: item.exchange_status_type,
            description: item.Media_Type_Descr,
            value: item.container_id,
            ...item
          };
        })
        .sort((a, b) => a.containerNumber.localeCompare(b.containerNumber));
      setContainers(list);
      RequestForm.setFieldValue("container", list);
    }
  };

  const getLogicalVaults = async () => {
    try {
      let reqBody = {
        main_district_id: districtId,
        customer_id: customerSelected
      };
      setIsLoading(true);

      let url = CF_URLS.disasterRecovery.getVaultMeadiaType;
      let res = await getResponseData(reqBody, url, 2);

      let data = res.data;
      if (data[0] && data[0][0]?.error && data[0][0]?.error !== zero) {
        handleError(data[0][0]?.error);
        setIsLoading(false);
        return;
      }

      let lvList = res?.data?.[0];
      let mtList = res?.data?.[1];
      lvList = lvList.map((lv) => ({
        label: lv.logical_vault_code,
        value: lv.logical_vault_id,
        type: lv.om_or_container_lv_flag
      }));
      setLogicalVaults(lvList.sort((a, b) => a.label.localeCompare(b.label)));

      mtList = mtList.map((mt) => ({
        label: mt.short_descr,
        value: mt.media_type_id,
        vaultId: mt.logical_vault_id
      }));
      setMedisTypes(mtList.sort((a, b) => a.label.localeCompare(b.label)));
    } catch (error) {
      handleCatchError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onChangeLogicalVault = (val) => {
    const changeEvent = () => {
      setSelectedLogicalVault(val);
      setSelectedMediaType((prev) => ({ ...defaultOption }));
    };

    if (hasSelected) {
      openConfirmPopup();
      setAwaitEvent(() => changeEvent);
    } else {
      changeEvent();
    }
  };

  const onChangeMediaType = (val) => {
    let changeEvent = () => {
      if (val?.value) {
        setSelectedMediaType(val);
      } else {
        setSelectedMediaType({ ...defaultOption });
      }
    };
    if (hasSelected) {
      openConfirmPopup();
      setAwaitEvent(() => changeEvent);
    } else {
      changeEvent();
    }
  };

  const getLogicalVaultsOptions = () => {
    let list = [];
    if (programType === programTypes.openMedia) {
      list = logicalVaults.filter((lv) => lv.type === programTypes.o);
    } else if (programType === programTypes.container) {
      list = logicalVaults.filter((lv) => lv.type === programTypes.c);
    }
    return list;
  };

  const getMediaTypeOptions = () => {
    return medisTypes.filter(
      (mt) => mt.vaultId === selectedLogicalVault?.value
    );
  };

  const onChangeProgramType = (val) => {
    const changeEvent = () => {
      setProgramType(val);
      setSelectedLogicalVault(null);
      setSelectedMediaType({ ...defaultOption });
    };

    if (hasSelected) {
      openConfirmPopup();
      setAwaitEvent(() => changeEvent);
    } else {
      changeEvent();
    }
  };

  const onClickFindButton = async () => {
    setErrorMessage("");
    generateForLogicalVault();
  };

  const options = {
    ...DEFAULT_DATA_TABLE_OPTIONS,
    pagination: true,
    sort: true,
    textLabels: {
      body: {
        noMatch: `${t("common.tableTextWithoutInputs")}`
      }
    }
  };

  const getMaxCountForGrid = async () => {
    try {
      const globalAttributeValue = await getGlobalAttributeValue(
        districtId,
        systemId,
        attributeTypeId
      );
      setMaxDataCount(globalAttributeValue);
    } catch (error) {
      handleCatchError(error);
    }
  };

  const handleSelectChangeOpenMedia = (e, number) => {
    let checked = e.target.checked;
    let updatedIndex = openMediaList.findIndex(
      (item) => item.openMedia === number
    );
    let updatedOpenMediaList = [...openMediaList];
    updatedOpenMediaList[updatedIndex].selected = checked;
    let selectedList = updatedOpenMediaList.filter((om) => !!om.selected);
    RequestForm.setFieldValue("openMedia", selectedList);
    setOpenMediaList(updatedOpenMediaList);
  };

  const openMediaColumns = [
    {
      name: "selected",
      options: {
        customHeadLabelRender: () => VALUE_EMPTY_STRING,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          let volserNumber = tableMeta?.rowData[1];
          return (
            <Stack sx={disasterRecoveryStyles.inboundDate.actionColumn}>
              <Checkbox
                checked={value}
                onChange={(e) => handleSelectChangeOpenMedia(e, volserNumber)}
              />
            </Stack>
          );
        },
        headerNoWrap: true
      },
      label: ""
    },
    ...openMediaColumnsList
  ];

  const handleSelectChange = (e, number) => {
    let checked = e.target.checked;
    let updatedIndex = containers.findIndex(
      (item) => item.containerNumber === number
    );
    let updatedConatiners = [...containers];
    updatedConatiners[updatedIndex].selected = checked;
    let selectedList = updatedConatiners.filter((c) => !!c.selected);
    RequestForm.setFieldValue("container", selectedList);
    setContainers(updatedConatiners);
  };

  const containerColumns = [
    {
      name: "selected",
      options: {
        customHeadLabelRender: () => VALUE_EMPTY_STRING,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          let containerNumber = tableMeta?.rowData[1];
          return (
            <Stack sx={disasterRecoveryStyles.inboundDate.actionColumn}>
              <Checkbox
                checked={value}
                onChange={(e) => handleSelectChange(e, containerNumber)}
              />
            </Stack>
          );
        },
        headerNoWrap: true
      },
      label: ""
    },
    ...containerColumnsList
  ];

  const onCLickYesFromConfirm = async () => {
    mapDataFromRes(resData);
    setShowConfirmModal(false);
    setConfirmMessage(VALUE_EMPTY_STRING);
  };

  const onClickNoFromConfirm = () => {
    setShowConfirmModal(false);
    setConfirmMessage(VALUE_EMPTY_STRING);
  };

  const getModalButtons = () => {
    return (
      <>
        <Button variant="outlined" onClick={onCLickYesFromConfirm}>
          {t("common.yes")}
        </Button>
        <Button variant="contained" onClick={onClickNoFromConfirm}>
          {t("common.no")}
        </Button>
      </>
    );
  };

  const handelAddRequest = () => {
    addAllSelectedOpenMedia();
    onClickClear();
  };

  const getUniqueByProperty = (array, prop) => {
    const seen = new Set();
    return array.filter((item) => {
      const trimmedNumber = item[prop]?.trim();
      if (seen.has(trimmedNumber)) {
        return false;
      } else {
        seen.add(trimmedNumber);
        return true;
      }
    });
  };

  const addAllSelectedOpenMedia = () => {
    const selectedCustomerOpenMedia = openMediaList.filter((o) => o.selected);

    const selectedContainers = containers.filter((c) => c.selected);

    setAddOpenMedia((prev) =>
      getUniqueByProperty([...prev, ...selectedCustomerOpenMedia], volser)
    );
    setAddContainer((prev) =>
      getUniqueByProperty([...prev, ...selectedContainers], containerNumber)
    );
  };

  const handelClearSelected = () => {
    const updatedOpenMediaList = openMediaList.map((openMedia) => {
      openMedia.selected = false;
      return openMedia;
    });

    const updatedContainers = containers.map((c) => ({
      ...c,
      selected: false
    }));
    setContainers(updatedContainers);
    setOpenMediaList(updatedOpenMediaList);
  };

  const handelReviewAndGenerate = () => {
    addAllSelectedOpenMedia();
    RequestForm.setFieldValue("buildRequest", buildType.landingPage);
  };

  const openConfirmPopup = async () => {
    let { errorMsg } = await getTheFirestoreErrorMessage(
      errorMsgs.errorCode26084
    );
    setConfirmPopupMessage(errorMsg);
    setConfirmPopup(true);
  };

  useEffect(() => {
    if (hasSelected) {
      RequestForm.setFieldValue("hasLogicalVaultDataToAdd", true);
    } else {
      RequestForm.setFieldValue("hasLogicalVaultDataToAdd", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMediaList, containers]);

  const handleConfirmModalCancel = () => {
    setConfirmPopup(false);
  };
  const handleConfirmModalNo = () => {
    setIsFindDisabled(false);
    setOpenMediaList([]);
    setContainers([]);
    setShowGrid(false);
    setFoundFieldsState({});
    RequestForm.setFieldValue("openMedia", []);
    RequestForm.setFieldValue("container", []);
    RequestForm.setFieldValue("hasLogicalVaultDataToAdd", false);
    if (typeof awaitEvent === "function") awaitEvent();
    setConfirmPopup(false);
  };

  const handleConfirmModalYes = () => {
    addAllSelectedOpenMedia();
    handleConfirmModalNo();
  };

  return (
    <>
      {isLoading && <FullCircularLoader />}
      <CommonModal
        open={showConfirmModal}
        buttons={getModalButtons()}
        title={t("common.confirm")}
        body={<Typography>{confirmMessage}</Typography>}
      />
      <Box
        ml={spacing.gap}
        sx={{
          ...disasterRecoveryStyles.requestGeneratorStyles.rightPanelWrapper,
          ...disasterRecoveryStyles.inboundDate.mainWrapper
        }}
      >
        <Box>
          {!!errorMessage && (
            <Collapse in={!!errorMessage}>
              <Alert
                severity={errorType}
                icon={
                  errorType === severity.error ? (
                    <img
                      src={ErrorIcon}
                      alt="error"
                      style={disasterRecoveryStyles.commonStyles.errorIcon}
                    />
                  ) : null
                }
                action={
                  <IconButton
                    aria-label={t("common.close")}
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setErrorMessage(VALUE_EMPTY_STRING);
                    }}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
              >
                {errorMessage}
              </Alert>
            </Collapse>
          )}
          <Grid container gap={spacing.gap}>
            <Grid item sx={12} md={12} xl={12}>
              <Stack gap={spacing.gap10}>
                <Typography
                  style={disasterRecoveryStyles.commonStyles.subHeading}
                >
                  {t("disasterRecovery.eventDetail.logicalVaultHeading")}
                </Typography>
                <FormLabel>{helperText}</FormLabel>
              </Stack>
            </Grid>
            <Grid item sx={4}>
              <Stack direction="row" gap={spacing.gap}>
                <FormControl>
                  <FormLabel component="legend">
                    {t("disasterRecovery.eventDetail.media")}
                  </FormLabel>
                  <RadioGroup
                    row
                    name="media-radio-buttons-group"
                    value={programType}
                    onChange={(e) => onChangeProgramType(e.target.value)}
                    sx={disasterRecoveryStyles.commonStyles.radioGroup}
                  >
                    <FormControlLabel
                      value={programTypes.openMedia}
                      control={<Radio />}
                      label={t("disasterRecovery.eventDetail.rgOpenMedia")}
                    />
                    <FormControlLabel
                      value={programTypes.container}
                      control={<Radio />}
                      label={t("disasterRecovery.eventDetail.containers")}
                    />
                  </RadioGroup>
                </FormControl>
              </Stack>
            </Grid>
            <Grid container gap={spacing.gap}>
              <Grid item xs={4}>
                <Autocomplete
                  componentsProps={{
                    clearIndicator: ArrowDown
                  }}
                  popupIcon={<ArrowDown />}
                  clearIcon={<CloseIcon />}
                  id="logical-vault"
                  disablePortal
                  name={"logicalVault"}
                  onChange={(e, val) => onChangeLogicalVault(val)}
                  options={getLogicalVaultsOptions()}
                  size="medium"
                  value={selectedLogicalVault}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("disasterRecovery.eventDetail.logicalVaultReq")}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <Autocomplete
                  componentsProps={{
                    clearIndicator: ArrowDown
                  }}
                  popupIcon={<ArrowDown />}
                  clearIcon={<CloseIcon />}
                  id="media-type"
                  disablePortal
                  name={"mediaType"}
                  onChange={(e, val) => onChangeMediaType(val)}
                  options={[defaultOption, ...getMediaTypeOptions()]}
                  size="medium"
                  value={selectedMediaType}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("disasterRecovery.eventDetail.mediaType")}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
          <Stack gap={spacing.gap} mt={spacing.gap} direction="row">
            <Button variant="outlined" onClick={onClickClear}>
              {t("common.clear")}
            </Button>
            <Button
              variant="contained"
              onClick={onClickFindButton}
              disabled={selectedLogicalVault?.value ? isFindDisabled : true}
            >
              {t("common.find")}
            </Button>
          </Stack>
          {showGrid ? (
            <>
              <Box my={spacing.gap}>
                <Divider />
              </Box>

              <Stack gap={spacing.gap10}>
                <Typography
                  style={disasterRecoveryStyles.commonStyles.subHeading}
                >
                  {t("disasterRecovery.eventDetail.logicalVault")}
                </Typography>
                <FormLabel>
                  <span style={disasterRecoveryStyles.inboundDate.noteText}>
                    {t("disasterRecovery.eventDetail.addToRequestText")}
                  </span>
                </FormLabel>
              </Stack>

              <Stack gap={spacing.gap10} my={spacing.gap}>
                <Typography
                  style={disasterRecoveryStyles.commonStyles.subHeading}
                >
                  {t("disasterRecovery.eventDetail.rgOpenMedia")}
                </Typography>
                <Paper>
                  <TableContainer>
                    <Box sx={disasterRecoveryStyles.inboundDate.tableContainer}>
                      <MUIDataTable
                        columns={openMediaColumns}
                        options={options}
                        data={openMediaList}
                        title={
                          <Typography variant="subtitle1">
                            {getSelectedOpenMediaCount()}{" "}
                            {t(
                              "disasterRecovery.eventDetail.openMediaSelected"
                            )}
                          </Typography>
                        }
                      />
                    </Box>
                  </TableContainer>
                </Paper>
              </Stack>

              <Stack gap={spacing.gap10}>
                <Typography
                  style={disasterRecoveryStyles.commonStyles.subHeading}
                >
                  {t("disasterRecovery.eventDetail.containers")}
                </Typography>
                <Paper>
                  <TableContainer>
                    <Box sx={disasterRecoveryStyles.inboundDate.tableContainer}>
                      <MUIDataTable
                        columns={containerColumns}
                        options={options}
                        data={containers}
                        title={
                          <Typography variant="subtitle1">
                            {getSelectedContainersCount()}{" "}
                            {t("disasterRecovery.eventDetail.containers")}
                          </Typography>
                        }
                      />
                    </Box>
                  </TableContainer>
                </Paper>
              </Stack>
            </>
          ) : null}
        </Box>

        <Box
          sx={disasterRecoveryStyles.tableStyles.displayFlex}
          gap={spacing.gap}
          mt={spacing.gap}
        >
          {showGrid ? (
            <>
              <Button
                variant="outlined"
                id="add-request"
                style={disasterRecoveryStyles.tableStyles.buttonsSpace}
                disabled={!hasSelected}
                onClick={handelAddRequest}
              >
                {t("disasterRecovery.eventDetail.addToRequest")}
              </Button>
              <Button
                variant="outlined"
                id="clear-selection"
                style={disasterRecoveryStyles.tableStyles.buttonsSpace}
                disabled={!hasSelected}
                onClick={handelClearSelected}
              >
                {t("disasterRecovery.eventDetail.clearSelected")}
              </Button>
            </>
          ) : null}
          <Button
            variant="contained"
            id="review-generate-request"
            style={disasterRecoveryStyles.tableStyles.buttonsSpace}
            onClick={handelReviewAndGenerate}
          >
            {t("disasterRecovery.eventDetail.reviewGenerateRequest")}
          </Button>
        </Box>
      </Box>
      <ConfirmPopup
        message={confirmPopupMessage}
        modalPopupOpen={confirmPopup}
        handleYes={() => handleConfirmModalYes()}
        handleNo={() => handleConfirmModalNo()}
        handleCancel={() => handleConfirmModalCancel()}
        showNo={true}
        showCancel={true}
      />
    </>
  );
};

export default LogicalVaultRequest;
